// frontend/packages/business-console/src/components/SubscriptionCancel.jsx
//
// Self-serve "cancel anytime" for the tenant's UpRyzze plan. Cancelling
// schedules the end at the close of the period already paid for (or the free
// trial); nothing is charged after that. "Keep my plan" withdraws it while the
// period is still running. Backend: POST /api/v2/business/billing/
// subscription/{cancel,resume}, businessadmin only, audited (D6).
//
// Renders nothing when there is no live plan. Like BillingSettings, role is
// not known client-side, so a non-admin sees the server's 403 message.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';

import {
  getBillingHistory,
  cancelSubscription,
  resumeSubscription,
} from '../services/billingHistoryService';

const LIVE = ['active', 'trialing', 'past_due'];

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function SubscriptionCancel() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getBillingHistory();
        if (active) setSub(res?.subscription || null);
      } catch {
        // BillingHistory already shows the unavailable state.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading || !sub || !LIVE.includes(sub.status)) return null;

  const endDate = formatDate(sub.currentPeriodEnd);
  const trialing = sub.status === 'trialing';

  const run = async (fn) => {
    setWorking(true);
    setError(null);
    try {
      const res = await fn();
      if (res?.subscription) setSub((s) => ({ ...s, ...res.subscription }));
      setConfirmOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 403
          ? 'Only business admins can change the plan.'
          : err?.response?.data?.message ||
              'Could not update your plan. Please try again.'
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <EventBusyOutlinedIcon
          fontSize="small"
          sx={{ color: 'text.secondary', mt: 0.5 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Your UpRyzze plan
          </Typography>
          {error ? (
            <Alert
              severity="error"
              sx={{ mt: 1 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          ) : null}
          {sub.cancelAtPeriodEnd ? (
            <>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Your plan is set to end
                {endDate ? ` on ${endDate}` : ' at the end of this period'}.
                Everything keeps working until then, and you will not be charged
                again.
              </Typography>
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 1.5 }}
                disabled={working}
                onClick={() => run(resumeSubscription)}
              >
                {working ? <CircularProgress size={16} /> : 'Keep my plan'}
              </Button>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                You can cancel anytime. Your plan keeps working until the end of{' '}
                {trialing ? 'your free trial' : 'the period you have paid for'},
                and nothing is charged after that.
              </Typography>
              <Button
                size="small"
                color="error"
                variant="outlined"
                sx={{ mt: 1.5 }}
                onClick={() => setConfirmOpen(true)}
              >
                Cancel plan
              </Button>
            </>
          )}
        </Box>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Cancel your plan?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Your site, leads, and Console keep working
            {endDate ? ` until ${endDate}` : ' until the end of this period'}.
            After that the plan ends and you are not charged again.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            You can change your mind any time before then with one click.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={working}>
            Keep my plan
          </Button>
          <Button
            color="error"
            onClick={() => run(cancelSubscription)}
            disabled={working}
          >
            {working ? <CircularProgress size={16} /> : 'Cancel plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
